import { CallExpressionShape, type Shape, VariableStatementShape } from '@servicenow/sdk-build-core'
import type { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'

/**
 * Parse an arrow function body, extracting variable declarations
 * and the return object. Supports both concise `() => ({...})` and
 * block-body `() => { const x = ...; return {...} }` forms.
 *
 * This is a domain-agnostic utility — it knows nothing about lanes,
 * activities, or any specific child type.
 */
export function parseArrowBodyEntries(arrow: ArrowFunctionShape): {
    variables: Map<string, CallExpressionShape>
    returnObj: Shape | undefined
} {
    const statements = arrow.getStatements()
    const variables = new Map<string, CallExpressionShape>()
    let returnObj: Shape | undefined

    // Extract variable declarations from statements (applies to both forms)
    for (const stmt of statements) {
        if (stmt.is(VariableStatementShape)) {
            const varStmt = stmt.as(VariableStatementShape)
            const init = varStmt.getInitializer()
            if (init.is(CallExpressionShape)) {
                variables.set(varStmt.getVariableName().getName(), init.as(CallExpressionShape))
            }
        }
    }

    // Check for returnValue (used in both concise and block body forms)
    const rv = arrow.getReturnValue()
    if (rv?.isObject()) {
        returnObj = rv
        // For concise form, also extract call expressions from the return object entries
        if (statements.length === 0) {
            for (const [key, val] of returnObj.asObject().entries()) {
                if (val.is(CallExpressionShape)) {
                    variables.set(key, val.as(CallExpressionShape))
                }
            }
        }
    } else if (!rv) {
        // No returnValue — check if last statement is an object (legacy concise pattern)
        const lastStmt = statements[statements.length - 1]
        if (lastStmt?.isObject()) {
            returnObj = lastStmt
            for (const [key, val] of returnObj.asObject().entries()) {
                if (val.is(CallExpressionShape)) {
                    variables.set(key, val.as(CallExpressionShape))
                }
            }
        }
    }

    return { variables, returnObj }
}
